import { useEffect, useId, useRef, useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ForgeProvider } from '@/lib/api/generated/model';
import { ExternalLink } from '@/shared/components/ExternalLink';

import { FORGE_COPY } from '../copy';
import { requiredPermissions, tokenPageUrl } from '../forgeAccounts';
import {
  isForgeTokenRefused,
  isForgeUnreachable,
  type useForgeAccounts,
} from '../hooks/useForgeAccounts';

/**
 * Paste a personal token for one forge host (spec 159 US1).
 *
 * The token lives in this component's state only — never a store, never the
 * browser's storage — and is gone the moment the form closes, on success or
 * Cancel. A refusal is said beside the field, with the text kept to fix.
 */
export function LinkTokenForm({
  provider,
  host,
  link,
  onDone,
}: {
  provider: ForgeProvider;
  host: string;
  link: ReturnType<typeof useForgeAccounts>['link'];
  onDone: () => void;
}) {
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [token, setToken] = useState('');
  const [shown, setShown] = useState(false);

  const error = link.error;
  const problem =
    isForgeTokenRefused(error) || isForgeUnreachable(error)
      ? error.message
      : null;

  // A refusal sends the person back to the field they need to fix.
  useEffect(() => {
    if (problem) input.current?.focus();
  }, [problem]);

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (link.isPending || token.trim() === '') return;
        link.mutate(
          { host, provider, token },
          {
            onSuccess: () => {
              setToken('');
              onDone();
            },
          },
        );
      }}
      className="flex flex-col gap-2"
    >
      <Label htmlFor={inputId}>{FORGE_COPY.tokenLabel}</Label>
      <div className="flex items-center gap-2">
        <Input
          ref={input}
          id={inputId}
          type={shown ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
            if (problem) link.reset();
          }}
          aria-invalid={problem !== null}
          aria-describedby={problem !== null ? `${hintId} ${errorId}` : hintId}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={shown ? FORGE_COPY.hide : FORGE_COPY.show}
          aria-pressed={shown}
          onClick={() => setShown((value) => !value)}
        >
          {shown ? (
            <EyeOffIcon aria-hidden="true" />
          ) : (
            <EyeIcon aria-hidden="true" />
          )}
        </Button>
      </div>
      <p id={hintId} className="text-muted-foreground text-xs">
        {requiredPermissions(provider)}{' '}
        <ExternalLink href={tokenPageUrl(provider, host)}>
          {FORGE_COPY.createOne(host)}
        </ExternalLink>
      </p>
      {problem !== null ? (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {problem}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={link.isPending || token.trim() === ''}>
          {link.isPending ? FORGE_COPY.saving : FORGE_COPY.save}
        </Button>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            setToken('');
            link.reset();
            onDone();
          }}
        >
          {FORGE_COPY.cancel}
        </Button>
      </div>
    </form>
  );
}
